// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { ActiveState, Jsonld, SeoProps } from "@bg/App/Widgets/SearchEngineOptimization/SearchEngineOptimization";
import { emptyGuid } from "@insite/client-framework/Common/StringHelpers";
import { getSelectedCategoryPath } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCatalogPageStateByPath } from "@insite/client-framework/Store/Data/CatalogPages/CatalogPagesSelectors";
import { getCategoryState } from "@insite/client-framework/Store/Data/Categories/CategoriesSelectors";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";

/**
 * Activate structure based on condition
 *
 * @param SeoProps
 * @returns ActiveState
 */
const activate = ({ state, fields, currentPage, payload }: SeoProps): ActiveState => {
    const allowedPages = currentPage === "Category Details" || currentPage === "Product List";

    if (allowedPages) {
        const location = getLocation(state);
        const categoryPath =
            getSelectedCategoryPath(state) ||
            (location.pathname.toLowerCase().startsWith("/content/") ? "" : location.pathname);
        const catalogPageState = getCatalogPageStateByPath(state, categoryPath);
        payload.category = getCategoryState(
            state,
            catalogPageState.value?.categoryIdWithBrandId ?? catalogPageState.value?.categoryId,
        )?.value;
    }

    return allowedPages && payload?.category?.subCategoryIds?.length;
};

interface ItemList {
    mainEntity?: any;
}

/**
 * Build the structured data
 *
 * @param SeoProps
 * @returns Jsonld
 */
const json = ({ state, url, payload }: SeoProps): Jsonld => {
    const subCategories = payload?.category?.subCategoryIds?.map((categoryId: string) => {
        return getCategoryState(state, categoryId).value;
    });
    const categories: any[] = [payload?.category, ...subCategories];

    const mainEntity = [] as any;
    const cloudianryPath = (state.context.settings.settingsCollection as any).customSettings.mediaRootPath;
    categories.forEach((item: any) => {
        const listItem: any = {
            "@type": "ItemList",
            name: item.shortDescription || item.name,
            image: cloudianryPath + item.smallImagePath,
            description: item.htmlContent?.replace(/(<([^>]+)>)/gi, "") || item.shortDescription,
            url: url + item.path,
        };

        if (item.subCategoryIds?.length) {
            listItem.itemListElement = [] as any;
            item.subCategoryIds.forEach((subCat: any) => {
                const category = getCategoryState(state, subCat).value;
                listItem.itemListElement.push({
                    "@type": "Thing",
                    name: category?.shortDescription || category?.name,
                    image: cloudianryPath + category?.smallImagePath,
                    description: category?.htmlContent?.replace(/(<([^>]+)>)/gi, "") || category?.shortDescription,
                    url: url + category?.path,
                });
            });
        }
        mainEntity.push(listItem);
    });

    return {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        name: payload.category.shortDescription,
        description: payload.category?.htmlContent?.replace(/(<([^>]+)>)/gi, "") || payload.category?.shortDescription,
        url: url + payload.category.path,
        mainEntity,
    };
};

export default { activate, json };
